import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GroupsIcon from '@mui/icons-material/Groups';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { SmartFilter } from '../../components/common/SmartFilter';
import Pagination from '../../components/atoms/Pagination';
import withComponentAuth from '../../hoc/withComponentAuth';
import useSnackbar from '../../hooks/useSnackbar';
import { debounce } from '../../utils/helper';
import { useUrlSearchQuery } from '../../hooks/useUrlSearchQuery';
import { ImageUpload } from '../../components/atoms/UploadImage';
import {
  ChannelPartnerSummary,
  PartnerStatus,
  getChannelPartners,
  getChannelPartnerStats,
  PartnerStatsResponse,
  createChannelPartner,
  CreateChannelPartnerPayload,
} from '../../services/channelPartners';
import {
  BUSINESS_TYPE_OPTIONS,
  PARTNER_TYPE_OPTIONS,
  partnerTypeLabel,
  BusinessFormState,
  AddressFormState,
  BankFormState,
  emptyBusinessForm,
  emptyAddressForm,
  emptyBankForm,
  validateBusinessForm,
  validateAddressForm,
  validateBankForm,
  businessFormToPayload,
  addressFormToPayload,
  bankFormToPayload,
} from './partnerFormFields';

const emptyPartnerForm: CreateChannelPartnerPayload = {
  fullName: '',
  phoneNumber: '',
  email: '',
  dateOfBirth: '',
  partnerType: '',
  profilePhoto: '',
};

// UK mobile national significant number: 10 digits, always starting with 7
// (mirrors the backend's `^\+447\d{9}$` requirement in Utils/ukValidators.js).
const sanitizeUkMobileDigits = (raw: string) => {
  let digits = raw.replace(/\D/g, '');
  if (digits.startsWith('0')) digits = digits.slice(1);
  return digits.slice(0, 10);
};

const isValidUkMobileDigits = (digits: string) => digits.length === 10 && digits[0] === '7';

const statuses: PartnerStatus[] = ['DRAFT', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'SUSPENDED'];

const statusMeta: Record<PartnerStatus, { label: string; color: string; bg: string }> = {
  DRAFT: { label: 'Draft', color: '#94a3b8', bg: '#64748b22' },
  UNDER_REVIEW: { label: 'Under review', color: '#f59e0b', bg: '#f59e0b22' },
  APPROVED: { label: 'Approved', color: '#10b981', bg: '#10b98122' },
  REJECTED: { label: 'Rejected', color: '#ef4444', bg: '#ef444422' },
  SUSPENDED: { label: 'Suspended', color: '#a855f7', bg: '#a855f722' },
};

const pretty = (value?: string | null) =>
  value ? value.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'N/A';

const StatusChip = ({ status }: { status: PartnerStatus }) => {
  const meta = statusMeta[status] || statusMeta.DRAFT;
  return <Chip label={meta.label} size="small" sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600 }} />;
};

const StatCard = ({ label, value, icon, color }: { label: string; value: number; icon: React.ReactNode; color: string }) => (
  <Paper sx={{ p: 2, minWidth: 150, flex: 1, bgcolor: '#171717', border: '1px solid #242424', borderRadius: 2 }}>
    <Box display="flex" alignItems="center" gap={1.5}>
      <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${color}20`, color }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={700} color="#fff" lineHeight={1.1}>{value}</Typography>
        <Typography variant="caption" color="#64748b">{label}</Typography>
      </Box>
    </Box>
  </Paper>
);

const ChannelPartners = () => {
  const navigate = useNavigate();
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<CreateChannelPartnerPayload>(emptyPartnerForm);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [phoneDigits, setPhoneDigits] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [creating, setCreating] = useState(false);
  const [includeMoreDetails, setIncludeMoreDetails] = useState(false);
  const [businessForm, setBusinessForm] = useState<BusinessFormState>(emptyBusinessForm);
  const [addressForm, setAddressForm] = useState<AddressFormState>(emptyAddressForm);
  const [bankForm, setBankForm] = useState<BankFormState>(emptyBankForm);
  const [partners, setPartners] = useState<ChannelPartnerSummary[]>([]);
  const [stats, setStats] = useState<PartnerStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PartnerStatus | ''>('');
  const [partnerType, setPartnerType] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  useUrlSearchQuery((query) => {
    setSearch(query);
    setPage(1);
  });

  // Guards against out-of-order responses: if the status/type/search/page
  // filters change again before an in-flight request resolves (e.g. clicking
  // through status tabs quickly), a slower earlier response landing after a
  // newer one would otherwise overwrite the list with stale, mismatched-tab
  // results. Only the response from the most recently issued fetch is applied.
  const fetchRequestId = React.useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = ++fetchRequestId.current;
    try {
      setLoading(true);
      const [list, nextStats] = await Promise.all([
        getChannelPartners({ page, limit: rowsPerPage, search, status, partnerType }),
        getChannelPartnerStats(),
      ]);
      if (requestId !== fetchRequestId.current) return;
      setPartners(list.data || []);
      setTotal(list.pagination?.total || 0);
      setStats(nextStats);
    } catch (error) {
      console.error('Failed to load channel partners:', error);
    } finally {
      if (requestId === fetchRequestId.current) setLoading(false);
    }
  }, [page, rowsPerPage, search, status, partnerType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const partnerTypes = useMemo(() => {
    const types = partners.map((p) => p.partnerType).filter(Boolean) as string[];
    return Array.from(new Set(types));
  }, [partners]);

  const handleSearch = debounce((value: string) => {
    setSearch(value);
    setPage(1);
  }, 500);

  const setField = (field: keyof CreateChannelPartnerPayload, value: string) =>
    setAddForm((prev) => ({ ...prev, [field]: value }));

  const handlePhoneChange = (value: string) => {
    const digits = sanitizeUkMobileDigits(value);
    setPhoneDigits(digits);
    setField('phoneNumber', digits ? `+44${digits}` : '');
  };

  const openAdd = () => {
    setAddForm(emptyPartnerForm);
    setPhoneDigits('');
    setPhoneTouched(false);
    setIncludeMoreDetails(false);
    setBusinessForm(emptyBusinessForm);
    setAddressForm(emptyAddressForm);
    setBankForm(emptyBankForm);
    setAddOpen(true);
  };

  const phoneInvalid = phoneTouched && phoneDigits.length > 0 && !isValidUkMobileDigits(phoneDigits);

  const handleCreate = async () => {
    setPhoneTouched(true);
    if (
      !addForm.fullName.trim() ||
      !addForm.email.trim() ||
      !addForm.dateOfBirth ||
      !isValidUkMobileDigits(phoneDigits)
    ) {
      showSnackbar('Name, a valid UK mobile number, email and date of birth are required', 'error');
      return;
    }

    let business: CreateChannelPartnerPayload['business'];
    let address: CreateChannelPartnerPayload['address'];
    let bank: CreateChannelPartnerPayload['bank'];
    if (includeMoreDetails) {
      const businessHasInput = businessForm.businessType || businessForm.legalName.trim();
      const addressHasInput = addressForm.postcode.trim() || addressForm.line1.trim() || addressForm.town.trim();
      const bankHasInput = bankForm.accountHolderName.trim() || bankForm.sortCode.trim() || bankForm.accountNumber.trim();
      if ((addressHasInput || bankHasInput) && !businessHasInput) {
        showSnackbar('Business details are required before address or bank details', 'error');
        return;
      }
      if (businessHasInput) {
        const businessError = validateBusinessForm(businessForm);
        if (businessError) return showSnackbar(businessError, 'error');
        business = businessFormToPayload(businessForm);
      }
      if (addressHasInput) {
        const addressError = validateAddressForm(addressForm);
        if (addressError) return showSnackbar(addressError, 'error');
        address = addressFormToPayload(addressForm);
      }
      if (bankHasInput) {
        const bankError = validateBankForm(bankForm);
        if (bankError) return showSnackbar(bankError, 'error');
        bank = bankFormToPayload(bankForm);
      }
    }

    try {
      setCreating(true);
      await createChannelPartner({
        ...addForm,
        partnerType: addForm.partnerType || undefined,
        business,
        address,
        bank,
      });
      showSnackbar('Channel partner created', 'success');
      setAddOpen(false);
      setPage(1);
      fetchData();
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to create partner', 'error');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {SnackbarComponent}
      <SmartFilter
        title="Channel Partners"
        createFilter={['SEARCH']}
        onSearch={handleSearch}
        extraActions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{
              backgroundColor: '#7B1FA2',
              '&:hover': { backgroundColor: '#5E167A' },
              textTransform: 'none',
              borderRadius: '10px',
            }}
          >
            Add Partner
          </Button>
        }
      />
      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
        <Box display="flex" gap={1.5} flexWrap="wrap">
          <StatCard label="Total" value={stats?.total || 0} icon={<GroupsIcon fontSize="small" />} color="#3b82f6" />
          <StatCard label="Under Review" value={stats?.byStatus.UNDER_REVIEW || 0} icon={<HourglassTopIcon fontSize="small" />} color="#f59e0b" />
          <StatCard label="Approved" value={stats?.byStatus.APPROVED || 0} icon={<CheckCircleOutlineIcon fontSize="small" />} color="#10b981" />
          <StatCard label="Suspended" value={stats?.byStatus.SUSPENDED || 0} icon={<BlockIcon fontSize="small" />} color="#a855f7" />
        </Box>

        <Paper sx={{ p: 1.5, bgcolor: '#171717', border: '1px solid #242424', borderRadius: 2 }}>
          <Box display="flex" gap={1} flexWrap="wrap" mb={1.5}>
            <Chip label="All" clickable onClick={() => { setStatus(''); setPage(1); }} sx={{ bgcolor: status === '' ? '#7B1FA2' : '#242424', color: '#fff' }} />
            {statuses.map((item) => (
              <Chip key={item} label={statusMeta[item].label} clickable onClick={() => { setStatus(item); setPage(1); }} sx={{ bgcolor: status === item ? statusMeta[item].bg : '#242424', color: status === item ? statusMeta[item].color : '#94a3b8' }} />
            ))}
            {partnerTypes.map((type) => (
              <Chip key={type} label={partnerTypeLabel(type)} variant="outlined" clickable onClick={() => { setPartnerType(partnerType === type ? '' : type); setPage(1); }} sx={{ borderColor: partnerType === type ? '#7B1FA2' : '#333', color: partnerType === type ? '#fff' : '#94a3b8' }} />
            ))}
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  {['Name', 'Phone', 'Type', 'Status', 'Step', 'Submitted', 'Action'].map((head) => (
                    <TableCell key={head} sx={{ color: '#64748b', borderColor: '#242424' }} align={head === 'Action' ? 'right' : 'left'}>{head}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} sx={{ color: '#94a3b8', borderColor: '#242424' }}>Loading channel partners...</TableCell></TableRow>
                ) : partners.length === 0 ? (
                  <TableRow><TableCell colSpan={7} sx={{ color: '#94a3b8', borderColor: '#242424' }}>No channel partners found</TableCell></TableRow>
                ) : partners.map((partner) => (
                  <TableRow key={partner.id} hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }} onClick={() => navigate(`/channel-partners/${partner.id}`)}>
                    <TableCell sx={{ borderColor: '#242424' }}>
                      <Typography color="#e2e8f0" fontWeight={600} fontSize={14}>{partner.fullName || 'Unnamed partner'}</Typography>
                      <Typography color="#64748b" fontSize={12}>{partner.email || 'No email'}</Typography>
                    </TableCell>
                    <TableCell sx={{ color: '#94a3b8', borderColor: '#242424' }}>{partner.phoneNumber}</TableCell>
                    <TableCell sx={{ color: '#94a3b8', borderColor: '#242424' }}>{partnerTypeLabel(partner.partnerType)}</TableCell>
                    <TableCell sx={{ borderColor: '#242424' }}><StatusChip status={partner.derivedStatus || partner.status} /></TableCell>
                    <TableCell sx={{ color: '#94a3b8', borderColor: '#242424' }}>{pretty(partner.onboardingStep)}</TableCell>
                    <TableCell sx={{ color: '#94a3b8', borderColor: '#242424' }}>{partner.submitted ? dayjs(partner.updatedAt).format('DD MMM YYYY') : 'Not submitted'}</TableCell>
                    <TableCell align="right" sx={{ borderColor: '#242424' }}>
                      <Button size="small" startIcon={<VisibilityIcon />} onClick={(e) => { e.stopPropagation(); navigate(`/channel-partners/${partner.id}`); }}>View</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Pagination count={total} page={page - 1} rowsPerPage={rowsPerPage} onPageChange={(_, newPage) => setPage(newPage + 1)} onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(1); }} />
        </Paper>
      </Box>

      <Dialog
        open={addOpen}
        onClose={() => !creating && setAddOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#171717',
            color: '#fff',
            border: '1px solid #242424',
            borderRadius: 3,
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: 700,
          }}
        >
          Add Channel Partner
          <IconButton onClick={() => setAddOpen(false)} sx={{ color: '#94a3b8' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ borderColor: '#242424' }} />
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
            <Box sx={{ maxWidth: 160 }}>
              <ImageUpload
                imageUrl={addForm.profilePhoto || ''}
                onChange={(url) => setField('profilePhoto', url)}
                onRemove={() => setField('profilePhoto', '')}
                setLoader={setPhotoUploading}
                uploadLabel="Profile photo"
                fileId="partner-profile-photo-create"
                type="icon"
                folderType={16}
                disabled={photoUploading}
              />
            </Box>
            <TextField
              label="Full name"
              value={addForm.fullName}
              onChange={(e) => setField('fullName', e.target.value)}
              fullWidth
            />
            <TextField
              label="Mobile number"
              value={phoneDigits}
              onChange={(e) => handlePhoneChange(e.target.value)}
              onBlur={() => setPhoneTouched(true)}
              placeholder="7123 456789"
              error={phoneInvalid}
              helperText={phoneInvalid ? 'Enter a valid UK mobile number (10 digits, starting with 7)' : ' '}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ color: '#94a3b8', fontWeight: 600, pr: 0.5, borderRight: '1px solid #333' }}>
                      +44
                    </Typography>
                  </InputAdornment>
                ),
              }}
              inputProps={{ maxLength: 11, inputMode: 'numeric' }}
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={addForm.email}
              onChange={(e) => setField('email', e.target.value)}
              fullWidth
            />
            <TextField
              label="Date of birth"
              type="date"
              value={addForm.dateOfBirth}
              onChange={(e) => setField('dateOfBirth', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              select
              label="Partner type (optional)"
              value={addForm.partnerType || ''}
              onChange={(e) => setField('partnerType', e.target.value)}
              fullWidth
            >
              <MenuItem value="">None</MenuItem>
              {PARTNER_TYPE_OPTIONS.map((t) => (
                <MenuItem key={t} value={t}>
                  {partnerTypeLabel(t)}
                </MenuItem>
              ))}
            </TextField>

            <Button
              onClick={() => setIncludeMoreDetails((v) => !v)}
              sx={{ alignSelf: 'flex-start', color: '#9c7ac7', textTransform: 'none' }}
            >
              {includeMoreDetails ? '− Hide business, address & bank details' : '+ Add business, address & bank details'}
            </Button>

            <Collapse in={includeMoreDetails}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Divider sx={{ borderColor: '#242424' }}><Typography variant="caption" color="#64748b">Business</Typography></Divider>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <TextField
                      select
                      fullWidth
                      label="Business type"
                      value={businessForm.businessType}
                      onChange={(e) => setBusinessForm((p) => ({ ...p, businessType: e.target.value as BusinessFormState['businessType'] }))}
                    >
                      <MenuItem value="">None</MenuItem>
                      {BUSINESS_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={6}><TextField fullWidth label="Business Name" value={businessForm.legalName} onChange={(e) => setBusinessForm((p) => ({ ...p, legalName: e.target.value }))} /></Grid>
                  <Grid item xs={6}><TextField fullWidth label="Trading name" value={businessForm.tradingName} onChange={(e) => setBusinessForm((p) => ({ ...p, tradingName: e.target.value }))} /></Grid>
                  <Grid item xs={6}><TextField fullWidth label="VAT number" value={businessForm.vatNumber} onChange={(e) => setBusinessForm((p) => ({ ...p, vatNumber: e.target.value }))} /></Grid>
                  {businessForm.businessType === 'LIMITED_COMPANY' && (
                    <Grid item xs={6}><TextField fullWidth label="Company Registration No." value={businessForm.companiesHouseNumber} onChange={(e) => setBusinessForm((p) => ({ ...p, companiesHouseNumber: e.target.value }))} /></Grid>
                  )}
                  {businessForm.businessType === 'SOLE_TRADER' && (
                    <Grid item xs={6}><TextField fullWidth label="UTR" value={businessForm.utr} onChange={(e) => setBusinessForm((p) => ({ ...p, utr: e.target.value }))} /></Grid>
                  )}
                </Grid>

                <Divider sx={{ borderColor: '#242424' }}><Typography variant="caption" color="#64748b">Address</Typography></Divider>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}><TextField fullWidth label="Line 1" value={addressForm.line1} onChange={(e) => setAddressForm((p) => ({ ...p, line1: e.target.value }))} /></Grid>
                  <Grid item xs={12}><TextField fullWidth label="Line 2" value={addressForm.line2} onChange={(e) => setAddressForm((p) => ({ ...p, line2: e.target.value }))} /></Grid>
                  <Grid item xs={6}><TextField fullWidth label="Town" value={addressForm.town} onChange={(e) => setAddressForm((p) => ({ ...p, town: e.target.value }))} /></Grid>
                  <Grid item xs={6}><TextField fullWidth label="County" value={addressForm.county} onChange={(e) => setAddressForm((p) => ({ ...p, county: e.target.value }))} /></Grid>
                  <Grid item xs={6}><TextField fullWidth label="Postcode" value={addressForm.postcode} onChange={(e) => setAddressForm((p) => ({ ...p, postcode: e.target.value.toUpperCase() }))} /></Grid>
                </Grid>

                <Divider sx={{ borderColor: '#242424' }}><Typography variant="caption" color="#64748b">Bank</Typography></Divider>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}><TextField fullWidth label="Account holder" value={bankForm.accountHolderName} onChange={(e) => setBankForm((p) => ({ ...p, accountHolderName: e.target.value }))} /></Grid>
                  <Grid item xs={6}><TextField fullWidth label="Sort Code" placeholder="Sort Code" value={bankForm.sortCode} onChange={(e) => setBankForm((p) => ({ ...p, sortCode: e.target.value.replace(/\D/g, '').slice(0, 6) }))} /></Grid>
                  <Grid item xs={6}><TextField fullWidth label="Account No." placeholder="Account No." value={bankForm.accountNumber} onChange={(e) => setBankForm((p) => ({ ...p, accountNumber: e.target.value.replace(/\D/g, '').slice(0, 8) }))} /></Grid>
                </Grid>
              </Box>
            </Collapse>
          </Box>
        </DialogContent>
        <Divider sx={{ borderColor: '#242424' }} />
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setAddOpen(false)}
            disabled={creating}
            sx={{ color: '#94a3b8', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating}
            sx={{
              backgroundColor: '#7B1FA2',
              '&:hover': { backgroundColor: '#5E167A' },
              textTransform: 'none',
              borderRadius: '10px',
            }}
          >
            {creating ? 'Creating…' : 'Create Partner'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default withComponentAuth(ChannelPartners);
